import React, { useEffect, useState } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { motion } from 'framer-motion';
import Navbar from '../Navbar/Navbar';
import './Contact.css';

const API_URL = process.env.REACT_APP_API_URL

// Custom hook for media query
function useMediaQuery(query) {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) setMatches(media.matches);
    const listener = () => setMatches(media.matches);
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, [query, matches]);

  return matches;
}

const Contact = () => {
  const isLargeScreen = useMediaQuery('(min-width: 1024px)');

  // Contact Form State and Logic
  const [form, setForm] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [formSuccess, setFormSuccess] = useState("");

  const handleInputChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const validateForm = () => {
    const errors = {};
    if (!form.name.trim()) errors.name = "Name is required";
    if (!form.email.trim()) errors.email = "Email is required";
    else if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(form.email))
      errors.email = "Enter a valid email";
    if (!form.message.trim()) errors.message = "Message is required";
    return errors;
  };

  // const handleFormSubmit = (e) => {
  //   e.preventDefault();
  //   const errors = validateForm();
  //   setFormErrors(errors);

  //   if (Object.keys(errors).length === 0) {
  //     // Here you could POST to an API
  //     setFormSuccess("Thank you! Your message has been sent.");
  //     setForm({ name: "", email: "", message: "" });
  //     setTimeout(() => setFormSuccess(""), 5000);
  //   }
  // };

  const handleFormSubmit = async (e) => {
  e.preventDefault();
  const errors = validateForm();
  setFormErrors(errors);

  if (Object.keys(errors).length === 0) {
    try {
      const response = await fetch(`${API_URL}/public/api/v0/contact-admin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          message: form.message,
        }),
      });
      if (response.ok) {
        setFormSuccess("Thank you! Your message has been sent.");
        setForm({ name: "", email: "", message: "" });
        setTimeout(() => setFormSuccess(""), 5000);
      } else {
        setFormSuccess("Sorry, there was a problem sending your message.");
      }
    } catch (error) {
      setFormSuccess("Sorry, there was a problem sending your message.");
    }
  }
};


  // Animate sections on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
          }
        });
      },
      { threshold: 0.2 }
    );

    document.querySelectorAll('.fade-section').forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="contact-page">
      {isLargeScreen && <Navbar className="contact-navbar" />}
      <div className="contact-container">
        {/* Contact Header */}
        <section className="contact-header contact-fade-section">
          <Container className="text-center">
            <motion.h1
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: 'easeOut' }}
            >
              Contact us
            </motion.h1>
            <p className='text-dark'>We’re SAP and IT solution enthusiasts eager to get your problems solved.</p>
          </Container>
        </section>

        {/* Contact Info Section */}
        <section className="contact-info contact-fade-section">
          <Container>
            <Row className="gy-4">
              {/* Office Location */}
              <Col md={4}>
                <motion.div className="contact-box" whileHover={{ scale: 1.05 }}>
                  <div className="icon-box">📍</div>
                  <h5>Visit Our Office (Berlin)</h5>
                  <p><strong>Street:</strong> Pichelorferstr Str 61, 13595</p>
                  <p><strong>City:</strong> Berlin</p>
                  <p><strong>Country:</strong> Germany</p>
                </motion.div>
              </Col>

              {/* Contact Details */}
              <Col md={4}>
                <motion.div className="contact-box" whileHover={{ scale: 1.05 }}>
                  <div className="icon-box">📞</div>
                  <h5>Quick Contact</h5>
                  <p><strong>Phone:</strong> <a href="tel:+493035305602">+4930 35305602</a></p>
                  <p><a href="tel:+493035305604">+4930 35305604</a></p>
                  <p><strong>Email:</strong> <a href="mailto:info@ZNOTEC.com">info@ZNOTEC.com</a></p>
                </motion.div>
              </Col>

              {/* Working Hours */}
              <Col md={4}>
                <motion.div className="contact-box" whileHover={{ scale: 1.05 }}>
                  <div className="icon-box">⏳</div>
                  <h5>Working Hours</h5>
                  <p><strong>Monday – Friday:</strong> 8.00am to 5.00pm</p>
                  <p><strong>Saturday:</strong> We are Closed</p>
                  <p><strong>Sunday:</strong> We are Closed</p>
                </motion.div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Contact Form Section */}
        <section className="contact-form-section contact-fade-section">
          <Container>
            <Row className="justify-content-center">
              <Col md={8} lg={6}>
                <motion.div 
                  className="contact-form-box p-4 rounded shadow"
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, ease: 'easeOut' }}
                >
                  <h4 className="mb-3 text-center">Send us a message</h4>
                  <form onSubmit={handleFormSubmit} noValidate>
                    <div className="mb-3">
                      <label htmlFor="name" className="form-label">Name*</label>
                      <input
                        type="text"
                        className={`form-control ${formErrors.name ? "is-invalid" : ""}`}
                        id="name"
                        name="name"
                        value={form.name}
                        onChange={handleInputChange}
                        autoComplete="off"
                      />
                      {formErrors.name && <div className="invalid-feedback">{formErrors.name}</div>}
                    </div>
                    <div className="mb-3">
                      <label htmlFor="email" className="form-label">Email*</label>
                      <input
                        type="email"
                        className={`form-control ${formErrors.email ? "is-invalid" : ""}`}
                        id="email"
                        name="email"
                        value={form.email}
                        onChange={handleInputChange}
                        autoComplete="off"
                      />
                      {formErrors.email && <div className="invalid-feedback">{formErrors.email}</div>}
                    </div>
                    <div className="mb-3">
                      <label htmlFor="message" className="form-label">Message*</label>
                      <textarea
                        className={`form-control ${formErrors.message ? "is-invalid" : ""}`}
                        id="message"
                        name="message"
                        rows={4}
                        value={form.message}
                        onChange={handleInputChange}
                      />
                      {formErrors.message && <div className="invalid-feedback">{formErrors.message}</div>}
                    </div>
                    <button type="submit" className="btn btn-primary w-100">Send Message</button>
                    {formSuccess && (
                      <div className="alert alert-success mt-3 text-center">{formSuccess}</div>
                    )}
                  </form>
                </motion.div>
              </Col>
            </Row>
          </Container>
        </section>
      </div>
    </div>
  );
};

export default Contact;
